// TenantSwitcher — tenant pill in the navigation panel (restyled for the Console Glass shell, task 355.4).
// Rendered only when the authenticated user belongs to more than one tenant.
// On switch: replaces the access token and clears the React Query cache. When the target tenant asks for verification
// again (task 387.11.5), a step-up modal answers the challenge first and completes the switch.
// Does NOT navigate — page data refetches automatically via query invalidation.

import React, { useState } from 'react';
import { Dropdown, message, Spin, Typography, theme } from 'antd';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { ChevronDown } from 'lucide-react';
import type { MenuProps } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import type { MfaStep } from '@/api/contracts/session';
import type { SwitchTenantResponse } from '@/api/endpoints/auth';
import { useAuthStore } from '@/stores/authStore';
import StepUpModal from '../mfa/StepUpModal';
import { useSidebarStore } from '@/stores/sidebarStore';
import { normalizeError } from '@/shared/utils/errorHandler';
import styles from './TenantSwitcher.module.css';

const MAX_VISIBLE = 5;

/** 'Demo Financial Services Ltd' → 'DF' */
function tenantInitials(name: string): string {
  const words = name.split(/\s+/).filter((w) => /^[A-Za-z0-9]/.test(w));
  return (
    words
      .slice(0, 2)
      .map((w) => w[0])
      .join('') || '?'
  ).toUpperCase();
}

function TenantSwitcher(): React.ReactElement | null {
  const { availableTenants, currentTenantId, switchTenant, completeTenantSwitch } = useAuthStore();
  const { collapsed } = useSidebarStore();
  const [switching, setSwitching] = useState(false);
  const [stepUp, setStepUp] = useState<{ step: MfaStep; tenantId: string } | null>(null);
  const queryClient = useQueryClient();
  const { token } = theme.useToken();

  // Nothing to render when tenant list is unknown.
  if (availableTenants.length === 0) return null;

  // tenantId / tenantName are the names the server actually sends; reading `id`/`name` was half of
  // why tenant switching never worked (WEB-002, decision Q22).
  const currentTenant = availableTenants.find((t) => t.tenantId === currentTenantId);
  const displayName = currentTenant?.tenantName ?? currentTenantId ?? '—';

  const mark = tenantInitials(displayName);

  // Collapsed navigation: just the tenant mark.
  if (collapsed) {
    return (
      <div className={[styles.pill, styles.collapsed].join(' ')} title={displayName}>
        <span className={styles.mark}>{mark}</span>
      </div>
    );
  }

  // Single-tenant user: the pill is a label, not a control.
  if (availableTenants.length === 1) {
    return (
      <div className={styles.pill}>
        <span className={styles.mark}>{mark}</span>
        <span className={styles.text}>
          <span className={styles.name}>{displayName}</span>
          <span className={styles.hint}>Tenant</span>
        </span>
      </div>
    );
  }

  const handleSwitch = async (tenantId: string) => {
    if (tenantId === currentTenantId || switching) return;
    setSwitching(true);
    try {
      const step = await switchTenant(tenantId);
      if (step) setStepUp({ step, tenantId });
      else queryClient.clear();
    } catch (error) {
      void message.error((await normalizeError(error)).message || 'Failed to switch tenant');
    } finally {
      setSwitching(false);
    }
  };

  const handleSteppedUp = (result: SwitchTenantResponse): void => {
    completeTenantSwitch(result);
    setStepUp(null);
    queryClient.clear();
  };

  const stepUpTenantName = availableTenants.find(
    (t) => t.tenantId === stepUp?.tenantId,
  )?.tenantName;

  const visible = availableTenants.slice(0, MAX_VISIBLE);
  const overflowCount = availableTenants.length - MAX_VISIBLE;

  const menuItems: MenuProps['items'] = [
    ...visible.map((t) => ({
      key: t.tenantId,
      icon:
        t.tenantId === currentTenantId ? (
          <CheckOutlined style={{ color: token.colorPrimary }} />
        ) : (
          <span style={{ display: 'inline-block', width: 14 }} />
        ),
      label: t.tenantName,
    })),
    ...(overflowCount > 0
      ? [
          { type: 'divider' as const },
          {
            key: '__overflow__',
            label: (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {MAX_VISIBLE} of {availableTenants.length} — Search...
              </Typography.Text>
            ),
            disabled: true,
          },
        ]
      : []),
  ];

  return (
    <>
      <Dropdown
        menu={{
          items: menuItems,
          onClick: ({ key }) => {
            if (key !== '__overflow__') void handleSwitch(key);
          },
        }}
        disabled={switching}
        trigger={['click']}
      >
        <button
          type="button"
          className={styles.pill}
          aria-label={`Tenant: ${displayName}. Switch tenant`}
        >
          <span className={styles.mark}>
            {switching ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} />} size="small" />
            ) : (
              mark
            )}
          </span>
          <span className={styles.text}>
            <span className={styles.name}>{displayName}</span>
            <span className={styles.hint}>Tenant · switch</span>
          </span>
          <ChevronDown size={14} className={styles.chevron} aria-hidden="true" />
        </button>
      </Dropdown>
      <StepUpModal
        step={stepUp?.step ?? null}
        tenantName={stepUpTenantName}
        onSwitched={handleSteppedUp}
        onCancel={() => setStepUp(null)}
      />
    </>
  );
}

export default TenantSwitcher;
